import React, { useEffect, useState } from "react";
import "./viewRentalBooking.css";
import { ReactComponent as BackIcon } from "../../Assets/Categories/BackIcon.svg";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getRentalBookingView } from "../../Redux/Actions/rentalBookingActions";
import { addRewardPoints } from "../../Redux/Actions/bookingActions";
import { DateToLocale, ToFirstCapital } from "../../Utils/helpers";
import SearchBar from "../Main/searchBar";
import CustomRewardPointsPopup from "../Bookings/customPopup";
import toast from "react-hot-toast";

const Row = ({ label, value }) => {
    const empty = value === undefined || value === null || value === "";
    return (
        <div className="rvb-row">
            <span className="rvb-row__key">{label}</span>
            <span className={`rvb-row__val${empty ? " rvb-row__val--muted" : ""}`}>
                {empty ? "—" : value}
            </span>
        </div>
    );
};

const statusClass = (s = "") => {
    const v = s.toLowerCase();
    if (v === "completed") return "rvb-badge--confirmed";
    if (v === "pending" || v === "processing") return "rvb-badge--pending";
    if (v === "cancelled") return "rvb-badge--cancelled";
    return "rvb-badge--default";
};

const money = (currency, val) =>
    val !== undefined && val !== null ? `${currency || ""} ${Math.floor(val * 100) / 100}` : null;

const ViewRentalBooking = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [rewardPointsPopup, setRewardPointsPopup] = useState(false);
    const [confirmReward, setConfirmReward] = useState(false);
    const [pendingPoints, setPendingPoints] = useState(null);

    useEffect(() => {
        dispatch(getRentalBookingView(id));
    }, [id, dispatch]);

    const { rentalBookingDetail, loading } = useSelector((state) => state.rentalBooking || {});
    const booking = rentalBookingDetail?.data;

    const bookingStatus = booking?.booking_status || "";
    const currency = booking?.currency;

    const rewardPointsPopupHandler = () => setRewardPointsPopup(!rewardPointsPopup);

    const isCancelled = bookingStatus === "cancelled";
    const canReward = booking?.payment_status === "completed" && bookingStatus !== "cancelled";

    const handleRewardPointsSubmit = (points) => {
        setPendingPoints(points);
        setConfirmReward(true);
    };

    const handleRewardPointsSubmitConfirmed = () => {
        if (pendingPoints !== null) {
            dispatch(addRewardPoints({
                user_id: booking?.user?._id,
                reward_points: Number(pendingPoints),
                booking_id: booking?._id,
            })).then((res) => {
                if (res && res.status === "success") {
                    toast.success("Reward points assigned successfully!");
                    setRewardPointsPopup(false);
                    dispatch(getRentalBookingView(id));
                } else {
                    toast.error("Failed to assign reward points. Please try again.");
                }
                setConfirmReward(false);
                setPendingPoints(null);
            });
        }
    };

    return (
        <div className="rvb-root">

            <div className="header-top" style={{ marginLeft: "52%" }}>
                <SearchBar style={{ width: "max-content" }} />
            </div>

            <div className="rvb-header">
                <div className="rvb-header__left">
                    <button className="rvb-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
                        <BackIcon />
                    </button>
                    <div className="rvb-header__sep" />
                    <div className="rvb-header__meta">
                        <span className="rvb-header__booking-id">{booking?.booking_id || "—"}</span>
                        <span className="rvb-header__project-name">
                            {booking?.rental_property?.property_name || "No property"}
                        </span>
                    </div>
                </div>

                <div className="rvb-header__actions">
                    <button
                        className={`rvb-btn ${isCancelled ? "rvb-btn--disabled" : canReward ? "rvb-btn--ghost" : "rvb-btn--disabled"}`}
                        onClick={!isCancelled && canReward ? rewardPointsPopupHandler : undefined}
                        disabled={isCancelled || !canReward}
                        title={isCancelled ? "Cannot assign reward points on a cancelled booking" : ""}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                        Assign Reward Points
                    </button>
                    {booking?.booking_pdf_url && (
                        <a className="rvb-btn rvb-btn--ghost"
                            href={booking.booking_pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="9" y1="13" x2="15" y2="13" />
                                <line x1="9" y1="17" x2="15" y2="17" />
                            </svg>
                            Booking PDF
                        </a>
                    )}
                    {booking?.payment_link && booking?.payment_status !== "completed" && (
                        <a
                            className="rvb-btn rvb-btn--ghost"
                            href={booking.payment_link}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Payment Link
                        </a>
                    )}
                </div>
            </div>

            {rewardPointsPopup && (
                <CustomRewardPointsPopup
                    onClose={rewardPointsPopupHandler}
                    onSubmit={handleRewardPointsSubmit}
                />
            )}

            {confirmReward && (
                <div className="rvb-overlay">
                    <div className="rvb-modal rvb-modal--confirm">
                        <div className="rvb-modal__head">
                            <span className="rvb-modal__title">Confirm Reward Points</span>
                            <button
                                className="rvb-modal__x"
                                onClick={() => { setConfirmReward(false); setPendingPoints(null); }}
                            >✕</button>
                        </div>
                        <div className="rvb-modal__body">
                            <div className="rvb-confirm__icon">⭐</div>
                            <p className="rvb-confirm__text">
                                Assign <strong>{pendingPoints}</strong> reward points to this user?
                                This action cannot be undone.
                            </p>
                        </div>
                        <div className="rvb-modal__foot">
                            <button
                                className="rvb-btn rvb-btn--secondary"
                                onClick={() => { setConfirmReward(false); setPendingPoints(null); }}
                            >Cancel</button>
                            <button
                                className="rvb-btn rvb-btn--primary"
                                onClick={handleRewardPointsSubmitConfirmed}
                            >Confirm & Assign</button>
                        </div>
                    </div>
                </div>
            )}

            {loading && !booking && <div className="rvb-loading">Loading booking details…</div>}

            {booking && (
                <div className="rvb-grid">
                    {/* ── Booking Overview ── */}
                    <div className="rvb-card rvb-card--span2">
                        <div className="rvb-card__head">
                            <span className="rvb-card__title">
                                <span className="rvb-card__dot rvb-card__dot--blue" />
                                Booking Overview
                            </span>
                            <div className={`rvb-badge ${statusClass(bookingStatus)}`}>
                                <span className="rvb-badge__dot" />
                                {ToFirstCapital(bookingStatus) || "Unknown"}
                            </div>
                        </div>
                        <div className="rvb-card__body rvb-card__body--row">
                            {booking?.rental_property?.cover_photo && (
                                <div className="rvb-overview__aside">
                                    <img
                                        className="rvb-overview__img"
                                        src={booking.rental_property.cover_photo}
                                        alt="Property"
                                    />
                                </div>
                            )}
                            <div className="rvb-overview__fields">
                                <Row label="Booking ID" value={booking?.booking_id} />
                                <Row label="Booking Date" value={booking?.booking_date ? DateToLocale(booking.booking_date) : null} />
                                <Row label="Move-in Date" value={booking?.move_in_date ? DateToLocale(booking.move_in_date) : null} />
                                <Row label="User Type" value={ToFirstCapital(booking?.user_type)} />
                                <Row label="Payment Status" value={ToFirstCapital(booking?.payment_status)} />
                                <Row label="Payment Method" value={ToFirstCapital(booking?.payment_method)} />
                            </div>
                        </div>
                    </div>

                    {/* ── Property Details ── */}
                    <div className="rvb-card rvb-card--property rvb-card--span2">
                        <div className="rvb-card__head">
                            <span className="rvb-card__title">
                                <span className="rvb-card__dot rvb-card__dot--sky" />
                                Property
                            </span>
                            <span className="rvb-card__head-tag">#{booking?.rental_property?.property_id || "—"}</span>
                        </div>

                        {booking?.rental_property?.cover_photo && (
                            <div className="rvb-photos">
                                <img
                                    src={booking.rental_property.cover_photo}
                                    alt="Property cover"
                                    className="rvb-photos__img"
                                />
                            </div>
                        )}

                        <div className="rvb-card__body rvb-card__body--grid3">
                            <Row label="Property Name" value={booking?.rental_property?.property_name} />
                            <Row label="Street Address" value={booking?.rental_property?.street_address} />
                            <Row label="Locality" value={booking?.rental_property?.locality} />
                            <Row label="Zipcode" value={booking?.rental_property?.zipcode} />
                            <Row label="Furnishing" value={ToFirstCapital(booking?.rental_property?.furnished_status)} />
                            <Row label="Bedrooms" value={booking?.rental_property?.no_of_bedroom} />
                            <Row label="Bathrooms" value={booking?.rental_property?.no_of_bathroom} />
                            <Row
                                label="Lease Type"
                                value={
                                    Array.isArray(booking?.rental_property?.lease_type)
                                        ? booking.rental_property.lease_type.map(ToFirstCapital).join(", ")
                                        : null
                                }
                            />
                            <Row
                                label="Rent Amount"
                                value={money(currency, booking?.rental_property?.rent_amount)}
                            />
                            <Row label="Rent Frequency" value={ToFirstCapital(booking?.rental_property?.rent_frequency)} />
                        </div>
                    </div>

                    {/* ── User Details ── */}
                    <div className="rvb-card rvb-card--user">
                        <div className="rvb-card__head">
                            <span className="rvb-card__title">
                                <span className="rvb-card__dot rvb-card__dot--blue" />
                                User Details
                            </span>
                        </div>
                        <div className="rvb-card__body rvb-card__body--row">
                            <div className="rvb-avatar-wrap">
                                {booking?.user?.profile_image ? (
                                    <img className="rvb-avatar" src={booking.user.profile_image} alt="User" />
                                ) : (
                                    <div className="rvb-avatar rvb-avatar--initials">
                                        {(booking?.user?.name || "?")[0].toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="rvb-user-fields">
                                <Row label="Full Name" value={booking?.user?.name} />
                                <Row label="Email" value={booking?.user?.email} />
                                <Row
                                    label="Contact"
                                    value={
                                        booking?.user?.country_code && booking?.user?.contact_number
                                            ? `${booking.user.country_code} ${booking.user.contact_number}`
                                            : booking?.user?.contact_number
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Owner Details ── */}
                    <div className="rvb-card rvb-card--owner">
                        <div className="rvb-card__head">
                            <span className="rvb-card__title">
                                <span className="rvb-card__dot rvb-card__dot--teal" />
                                Owner Details
                            </span>
                        </div>
                        <div className="rvb-card__body rvb-card__body--row">
                            <div className="rvb-avatar-wrap">
                                {booking?.owner?.profile_image ? (
                                    <img className="rvb-avatar rvb-avatar--teal" src={booking.owner.profile_image} alt="Owner" />
                                ) : (
                                    <div className="rvb-avatar rvb-avatar--initials rvb-avatar--initials-teal">
                                        {(booking?.owner?.email || "?")[0].toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="rvb-user-fields">
                                <Row label="Name" value={booking?.owner?.name} />
                                <Row label="Email" value={booking?.owner?.email} />
                                <Row label="Contact" value={booking?.owner?.contact_number} />
                            </div>
                        </div>
                    </div>

                    {/* ── Payment Details ── */}
                    <div className="rvb-card rvb-card--payment rvb-card--span2">
                        <div className="rvb-card__head">
                            <span className="rvb-card__title">
                                <span className="rvb-card__dot rvb-card__dot--green" />
                                Payment Details
                            </span>
                        </div>
                        <div className="rvb-card__body rvb-card__body--grid3">
                            <Row label="Monthly Rent" value={money(currency, booking?.monthly_rent)} />
                            <Row label="Security" value={money(currency, booking?.security)} />
                            <Row label="Maintenance Fees" value={money(currency, booking?.maintanance_fees)} />
                            <Row label="Convenience Fee" value={money(currency, booking?.convenience_fee)} />
                            <Row label="Booking Amount" value={money(currency, booking?.booking_amount)} />
                            <Row label="Payment Gateway Amount" value={money(currency, booking?.payment_gateway_amount)} />
                            <Row label="Total Paid" value={money(currency, booking?.total)} />
                            <Row label="Domain Discount" value={money(currency, booking?.domain_discount)} />
                            <Row label="Domain Reward Credit" value={money(currency, booking?.domain_reward_credit)} />
                            <Row label="Domain Email" value={booking?.domain_email} />
                        </div>

                        {booking?.is_refund && (
                            <div className="rvb-card rvb-card--refund rvb-card--span2">
                                <div className="rvb-card__head">
                                    <span className="rvb-card__title">
                                        <span className="rvb-card__dot rvb-card__dot--red" />
                                        Refund Details
                                    </span>
                                    <div className={`rvb-badge ${statusClass("cancelled")}`}>
                                        <span className="rvb-badge__dot" />
                                        {ToFirstCapital(booking?.refund_status)} Refund
                                    </div>
                                </div>
                                <div className="rvb-card__body rvb-card__body--grid3">
                                    <Row label="Refund Amount" value={money(currency, booking?.refund_booking_amount)} />
                                    <Row label="Refund Type" value={ToFirstCapital(booking?.refund_status)} />
                                    <Row label="Refund In" value={ToFirstCapital(booking?.refund_in)} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewRentalBooking;